import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TextField,
    Grid,
    Box,
    Typography,
    alpha,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    Divider,
    IconButton,
    FormControl,
    InputLabel,
    OutlinedInput,
    Avatar,
} from '@mui/material';
import { Search, Close, Check, Person, Clear } from '@mui/icons-material';

// ============================================================================
// Types
// ============================================================================

type Teacher = {
    id: number;
    name: string;
    fullName: string;
};

type Props = {
    fullWidth?: boolean;
    teachers: Teacher[];
    value?: number | null;
    onChange: (teacher: Teacher | null) => void;
    cleanable?: boolean;
};

// ============================================================================
// Utility Functions
// ============================================================================

const normalizeText = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
};

const getInitials = (name: string): string => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2 && parts[0] && parts[1]) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (id: number): string => {
    const colors = [
        '#4CAF50',
        '#2196F3',
        '#FF5722',
        '#E91E63',
        '#9C27B0',
        '#00BCD4',
        '#FF9800',
        '#795548',
        '#607D8B',
        '#673AB7',
    ];
    return colors[id % colors.length] || '#9E9E9E';
};

// ============================================================================
// Dialog Component
// ============================================================================

type TeacherDialogProps = {
    open: boolean;
    teachers: Teacher[];
    selectedId?: number | null;
    onSelect: (teacher: Teacher | null) => void;
    onClose: () => void;
    cleanable?: boolean;
};

function TeacherDialog({
    open,
    teachers,
    selectedId,
    onSelect,
    onClose,
    cleanable,
}: TeacherDialogProps): React.ReactElement {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');

    const filteredTeachers = useMemo(() => {
        if (!search) return teachers;
        const normalized = normalizeText(search);
        return teachers.filter(
            (teacher) =>
                normalizeText(teacher.fullName).includes(normalized) ||
                normalizeText(teacher.name).includes(normalized),
        );
    }, [teachers, search]);

    // Reset search when dialog opens
    React.useEffect(() => {
        if (open) setSearch('');
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2, minHeight: '50vh', maxHeight: '70vh' },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    pb: 1,
                }}
            >
                <Typography variant="h6" component="span" fontWeight={600}>
                    {t('SelectTeacher')}
                </Typography>
                <TextField
                    size="small"
                    placeholder={t('Search')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{ flex: 1, maxWidth: 250 }}
                    autoFocus
                />
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2 }}>
                {filteredTeachers.length === 0 ? (
                    <Box
                        sx={{
                            py: 6,
                            textAlign: 'center',
                            color: 'text.secondary',
                        }}
                    >
                        <Person sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                        <Typography>{t('NoResults')}</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {cleanable && (
                            <Grid size={{ xs: 12 }}>
                                <Box
                                    onClick={() => {
                                        onSelect(null);
                                        onClose();
                                    }}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor:
                                            selectedId === null
                                                ? 'primary.dark'
                                                : 'divider',
                                        backgroundColor:
                                            selectedId === null
                                                ? alpha('#2196F3', 0.08)
                                                : 'background.paper',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        '&:hover': {
                                            borderColor: 'primary.dark',
                                            boxShadow: 1,
                                        },
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            bgcolor: 'grey.300',
                                        }}
                                    >
                                        <Clear />
                                    </Avatar>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="text.secondary"
                                    >
                                        {t('None')}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {filteredTeachers.map((teacher) => {
                            const color = getAvatarColor(teacher.id);
                            const isSelected = selectedId === teacher.id;

                            return (
                                <Grid key={teacher.id} size={{ xs: 12, sm: 6 }}>
                                    <Box
                                        onClick={() => {
                                            onSelect(teacher);
                                            onClose();
                                        }}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: isSelected
                                                ? color
                                                : 'divider',
                                            backgroundColor: isSelected
                                                ? alpha(color, 0.08)
                                                : 'background.paper',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            '&:hover': {
                                                borderColor: 'primary.dark',
                                                boxShadow: 1,
                                            },
                                        }}
                                    >
                                        {isSelected && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: '50%',
                                                    backgroundColor: color,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Check
                                                    sx={{
                                                        fontSize: 12,
                                                        color: 'white',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                bgcolor: color,
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {getInitials(teacher.fullName)}
                                        </Avatar>
                                        <Typography
                                            variant="body2"
                                            fontWeight={isSelected ? 600 : 500}
                                            sx={{
                                                color: isSelected
                                                    ? color
                                                    : 'text.primary',
                                            }}
                                        >
                                            {teacher.fullName}
                                        </Typography>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ============================================================================
// Main Component
// ============================================================================

export function SttSelectTeacher({
    fullWidth,
    teachers,
    value,
    onChange,
    cleanable = false,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);

    const selectedTeacher = useMemo(
        () => teachers.find((teacher) => teacher.id === value) || null,
        [teachers, value],
    );

    return (
        <Box>
            <FormControl
                variant="outlined"
                size="small"
                onClick={() => setDialogOpen(true)}
                style={{ width: fullWidth ? '100%' : 'auto' }}
                sx={{ cursor: 'pointer' }}
            >
                <InputLabel sx={{ cursor: 'pointer' }}>
                    {t('Teacher')}
                </InputLabel>
                <OutlinedInput
                    readOnly
                    inputProps={{
                        style: { cursor: 'pointer' },
                    }}
                    type="text"
                    value={selectedTeacher?.fullName || ''}
                    endAdornment={
                        <InputAdornment
                            position="end"
                            sx={{ cursor: 'pointer' }}
                        >
                            <Search />
                        </InputAdornment>
                    }
                    label={t('Teacher')}
                    sx={{
                        cursor: 'pointer',
                        '& input': { cursor: 'pointer' },
                    }}
                />
            </FormControl>
            <TeacherDialog
                open={dialogOpen}
                teachers={teachers}
                selectedId={value}
                onSelect={onChange}
                onClose={() => setDialogOpen(false)}
                cleanable={cleanable}
            />
        </Box>
    );
}
